'use client';

import { Menu as BaseMenu } from '@base-ui/react/menu';
import { type ElementType, useMemo } from 'react';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { MenuItemContext } from './internal/MenuItemContext';
import {
  menuItemBaseStyles,
  menuItemFocusStyles,
} from './internal/menuItemStyles';
import { useMenuItemInteraction } from './internal/useMenuItemInteraction';

const ELEMENT_TYPE = 'div';

const menuItemClasses = { root: 'zds-menu-item-root' } as const;

const menuItemStyles = tv({
  extend: menuItemBaseStyles,
  base: [menuItemClasses.root],
});

interface MenuItemBaseProps {
  /**
   * Called when the item is activated (click, Enter, or Space).
   */
  onClick?: () => void;
  /**
   * Text used for typeahead matching. Set when the item contains non-text
   * content (icons, badges) that would produce an incorrect match string.
   */
  typeaheadValue?: string;
  /**
   * Prevent the menu from closing when this item is activated.
   *
   * @default false
   */
  disableAutoClose?: boolean;
  /**
   * Disabled but still keyboard-focusable.
   *
   * @default false
   */
  isDisabled?: boolean;
}

type MenuItemProps<RootComponent extends ElementType = typeof ELEMENT_TYPE> =
  PolymorphicComponentProps<MenuItemBaseProps, RootComponent>;

type MenuItemComponent = PolymorphicComponent<
  MenuItemBaseProps,
  typeof ELEMENT_TYPE
>;

const MenuItem: MenuItemComponent = (props) => {
  const {
    onClick,
    typeaheadValue,
    disableAutoClose = false,
    isDisabled = false,
    className,
    children,
    component: Root = ELEMENT_TYPE,
    ...rest
  } = props;

  const { interactionProps } = useMenuItemInteraction({ isDisabled });
  const itemContext = useMemo(() => ({ isDisabled }), [isDisabled]);

  // Disabled link handling: strip href to prevent navigation,
  // set aria-disabled for screen readers, block onClick.
  const isLink = 'href' in props || 'to' in props;
  const disabledLinkProps =
    isDisabled && isLink ? { href: undefined, 'aria-disabled': true } : {};

  return (
    <MenuItemContext.Provider value={itemContext}>
      <BaseMenu.Item
        {...rest}
        label={typeaheadValue}
        closeOnClick={!disableAutoClose}
        disabled={isDisabled}
        onClick={isDisabled ? undefined : onClick}
        render={(baseProps) => (
          <Root
            {...mergeProps(baseProps, interactionProps)}
            {...disabledLinkProps}
            className={menuItemFocusStyles.root({
              className: menuItemStyles({ isDisabled, className }),
            })}
          />
        )}
      >
        {children}
      </BaseMenu.Item>
    </MenuItemContext.Provider>
  );
};

MenuItem.displayName = 'Menu.Item';

export {
  MenuItem,
  type MenuItemProps,
  type MenuItemBaseProps,
  menuItemClasses,
};
